import { ErrorText } from "./../common/errorText"
import { EditMode } from "../common/transaction"
import { AirportSeasonConfigurations } from "../api/AirportSeasonConfiguration"

const fieldTypes = {
  arrField: [
    "operatorArr",
    "serviceNoArr",
    "originStation",
    "lastStation",
    "clearedTimeArr",
    "serviceTypeArr",
    "offerTimeArr"
  ],
  depField: [
    "operatorDep",
    "serviceNoDep",
    "clearedTimeDep",
    "nextStation",
    "destinationStation",
    "serviceTypeDep",
    "offerTimeDep"
  ],
  turnaroundField: ["turnaroundDays", "minGroundTime"],
  genericField: ["numSeats", "aircraftType", "woop", "aircraftRegistration"],
  dateField: ["startDate", "endDate", "doop"]
}

export function handleGetType(id: string, types: Object): string {
  let type = null
  Object.keys(types).forEach((typeKey) => {
    const idExists = types[typeKey].find((a) => a === id)
    if (idExists) {
      type = typeKey
    }
  })
  return type
}

export interface TransactionItemFieldConfig {
  show: {
    pageModule: { add: boolean; edit: boolean; response: boolean }
    flightMode: { A: boolean; D: boolean; T: boolean }
  }
  editable: {
    pageModule: { add: boolean; edit: boolean; response: boolean }
    actionCode: { D: boolean; C: boolean; R: boolean; N: boolean }
  }
}

export interface TransactionItemFieldMap {
  [fieldName: string]: TransactionItemFieldConfig
}

const transactionItemFields: TransactionItemFieldMap = {
  arrField: {
    show: {
      pageModule: { add: true, edit: true, response: true },
      flightMode: { A: true, D: false, T: true }
    },
    editable: {
      pageModule: { add: true, edit: true, response: false },
      actionCode: { D: false, C: false, R: true, N: true }
    }
  },
  depField: {
    show: {
      pageModule: { add: true, edit: true, response: true },
      flightMode: { A: false, D: true, T: true }
    },
    editable: {
      pageModule: { add: true, edit: true, response: false },
      actionCode: { D: false, C: false, R: true, N: true }
    }
  },
  turnaroundField: {
    show: {
      pageModule: { add: true, edit: true, response: true },
      flightMode: { A: false, D: false, T: true }
    },
    editable: {
      pageModule: { add: true, edit: true, response: false },
      actionCode: { D: false, C: false, R: true, N: true }
    }
  },
  dateField: {
    show: {
      pageModule: { add: true, edit: true, response: true },
      flightMode: { A: true, D: true, T: true }
    },
    editable: {
      pageModule: { add: true, edit: true, response: false },
      actionCode: { D: true, C: true, R: true, N: true }
    }
  },
  genericField: {
    show: {
      pageModule: { add: true, edit: true, response: true },
      flightMode: { A: true, D: true, T: true }
    },
    editable: {
      pageModule: { add: true, edit: true, response: false },
      actionCode: { D: false, C: false, R: true, N: true }
    }
  },
  airport: {
    show: {
      pageModule: { add: true, edit: true, response: true },
      flightMode: { A: true, D: true, T: true }
    },
    editable: {
      pageModule: { add: true, edit: true, response: false },
      actionCode: { D: false, C: false, R: false, N: true }
    }
  },
  requiredTimeArr: {
    show: {
      pageModule: { add: false, edit: true, response: true },
      flightMode: { A: true, D: false, T: true }
    },
    editable: {
      pageModule: { add: false, edit: true, response: false },
      actionCode: { D: false, C: false, R: true, N: true }
    }
  },
  requiredTimeDep: {
    show: {
      pageModule: { add: false, edit: true, response: true },
      flightMode: { A: false, D: true, T: true }
    },
    editable: {
      pageModule: { add: false, edit: true, response: false },
      actionCode: { D: false, C: false, R: true, N: true }
    }
  },
  reasonCodeArr: {
    show: {
      pageModule: { add: false, edit: true, response: true },
      flightMode: { A: true, D: false, T: true }
    },
    editable: {
      pageModule: { add: false, edit: false, response: false },
      actionCode: { D: false, C: false, R: false, N: false }
    }
  },
  reasonCodeDep: {
    show: {
      pageModule: { add: false, edit: true, response: true },
      flightMode: { A: false, D: true, T: true }
    },
    editable: {
      pageModule: { add: false, edit: false, response: false },
      actionCode: { D: false, C: false, R: false, N: false }
    }
  }
}

export function handleGetTransactionItemFieldsConfig(): TransactionItemFieldMap {
  return transactionItemFields
}

export function showField(
  fieldName: string,
  flightMode: string,
  editMode: EditMode
): boolean {
  const fieldType = handleGetType(fieldName, fieldTypes) ?? fieldName
  return (
    transactionItemFields[fieldType].show.flightMode[flightMode] &&
    transactionItemFields[fieldType].show.pageModule[editMode]
  )
}

export function isFieldEditable(
  fieldName: string,
  code: string,
  editMode: EditMode
): boolean {
  const fieldType = handleGetType(fieldName, fieldTypes) ?? fieldName
  const actionCodeIsEditable = code
    ? transactionItemFields[fieldType].editable.actionCode[code]
    : true
  return (
    actionCodeIsEditable &&
    transactionItemFields[fieldType].editable.pageModule[editMode]
  )
}

export const lockedOrDisabled = (
  airportSeasons: AirportSeasonConfigurations,
  value: string,
  season: string
) => {
  const filteredAirportSeasonConfig = airportSeasons.filter(
    (a) => a.season === season
  )
  const lockedAirport = filteredAirportSeasonConfig.filter(
    (a) => a.isLocked === true
  )
  const disabledAirport = filteredAirportSeasonConfig.filter(
    (a) => a.isDisabled === true
  )
  const disabledAirportArr = disabledAirport.map((a: any) => a.airport)
  const lockedAirportArr = lockedAirport.map((a: any) => a.airport)
  const lockedOrDisabledArr = [...disabledAirportArr, ...lockedAirportArr]
  const disabledText = disabledAirportArr.includes(value)
  const lockedText = lockedAirportArr.includes(value)
  let errorMsg = null
  if (disabledText) {
    errorMsg = ErrorText.airportIsDisabled
  }
  if (lockedText) {
    errorMsg = ErrorText.airportIsLocked
  }

  return { lockedOrDisabledArr, errorMsg }
}

export const dateRangeIsValid = (startDate: string, endDate: string) => {
  const emptyStartDate = startDate !== null && startDate !== ""
  const emptyEndDate = endDate !== null && endDate !== ""
  let startDateValue = new Date(startDate)
  let endDateValue = new Date(endDate)

  const dateRangeError =
    emptyStartDate && emptyEndDate && startDateValue > endDateValue

  return dateRangeError
}
export const timeRangeIsValid = (from: string, to: string, length: number) => {
  const valid =
    from &&
    to &&
    from !== "" &&
    to !== "" &&
    from.length === length &&
    to.length === length &&
    Number(from) > Number(to)

  return valid
}
